import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Empty, message, Pagination, Table, Tooltip, Tag } from 'antd';
import { MakeApiCall } from '../../../../../apis';
import { ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import ProductDetailsModal from "../../../../../components/modal";
import { getAmount } from "../../../../../utils/helper";
import { buildCacheKey, getProductCache, setProductCache } from '../../../../../utils/Productcache';

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

export const FILTERABLE_COLUMNS = [
    { key: 'productSearch',    label: 'Product (SKU / ASIN / Title)', type: 'text'    },
    { key: 'tags',             label: 'Tags',                         type: 'text'    },
    { key: 'total_units',      label: 'Units Sold',                   type: 'numeric' },
    { key: 'refund_unit',      label: 'Refund Units',                 type: 'numeric' },
    { key: 'refund_cost',      label: 'Refund Cost',                  type: 'numeric' },
    { key: 'sales',            label: 'Sales',                        type: 'numeric' },
    { key: 'ad_sales',         label: 'Ad Sales',                     type: 'numeric' },
    { key: 'ad_spend',         label: 'Ad Spend',                     type: 'numeric' },
    { key: 'cogs',             label: 'COGS',                         type: 'numeric' },
    { key: 'organic_sales',    label: 'Organic Sales',                type: 'numeric' },
    { key: 'tax',              label: 'Tax',                          type: 'numeric' },
    { key: 'fees',             label: 'Fees',                         type: 'numeric' },
    { key: 'gross_profit',     label: 'Gross Profit',                 type: 'numeric' },
    { key: 'net_profit',       label: 'Net Profit',                   type: 'numeric' },
    { key: 'inventory',        label: 'Inventory',                    type: 'numeric' },
    { key: 'days_on_hand',     label: 'Days Until Stockout',          type: 'numeric' },
    { key: 'inventory_status', label: 'Inventory Health',             type: 'text'    },
    { key: 'status',           label: 'Status',                       type: 'text'    },
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const getFilterableValue = (row, key) => {
    switch (key) {
        case 'productSearch':
            return `${row.sku ?? ''} ${row.asin ?? ''} ${row.product_title ?? ''}`;
        case 'tags':
            return Array.isArray(row.tags) ? row.tags.join(' ') : '';
        case 'tax':
            return row?.more_data?.tax_liability?.total ?? 0;
        case 'status':
            return row.status === 0 ? 'Active' : 'Inactive';
        default:
            return row[key] ?? '';
    }
};

const applyFilters = (data, activeFilters) => {
    if (!activeFilters.length) return data;
    return data.filter(row =>
        activeFilters.every(f => {
            const cellValue = getFilterableValue(row, f.column);
            if (f.type === 'numeric') {
                const n = parseFloat(cellValue);
                const threshold = parseFloat(f.value);
                if (isNaN(threshold)) return true;
                if (f.operator === 'gte') return n >= threshold;
                if (f.operator === 'lte') return n <= threshold;
                if (f.operator === 'eq')  return n === threshold;
            } else {
                const s = String(cellValue).toLowerCase();
                const q = String(f.value).toLowerCase();
                if (f.operator === 'contains') return s.includes(q);
                if (f.operator === 'eq')       return s === q;
            }
            return true;
        })
    );
};

// ─── Sort comparators (static — defined outside component to stay stable) ───────
// antd uses these for sort-header UI (indicator + toggle).
// We ALSO use them directly to sort the full filteredProducts dataset so that
// sorting applies across all pages, not just the current page slice.
const COLUMN_SORTERS = {
    total_units:      (a, b) => (a.total_units      ?? 0) - (b.total_units      ?? 0),
    refund_unit:      (a, b) => (a.refund_unit       ?? 0) - (b.refund_unit       ?? 0),
    refund_cost:      (a, b) => (a.refund_cost       ?? 0) - (b.refund_cost       ?? 0),
    sales:            (a, b) => (a.sales             ?? 0) - (b.sales             ?? 0),
    ad_sales:         (a, b) => (a.ad_sales          ?? 0) - (b.ad_sales          ?? 0),
    ad_spend:         (a, b) => (a.ad_spend          ?? 0) - (b.ad_spend          ?? 0),
    cogs:             (a, b) => (a.cogs              ?? 0) - (b.cogs              ?? 0),
    organic_sales:    (a, b) => (a.organic_sales     ?? 0) - (b.organic_sales     ?? 0),
    tax:              (a, b) => (a?.more_data?.tax_liability?.total ?? 0) - (b?.more_data?.tax_liability?.total ?? 0),
    fees:             (a, b) => (a.fees              ?? 0) - (b.fees              ?? 0),
    gross_profit:     (a, b) => (a.gross_profit      ?? 0) - (b.gross_profit      ?? 0),
    net_profit:       (a, b) => (a.net_profit        ?? 0) - (b.net_profit        ?? 0),
    inventory:        (a, b) => (a.inventory         ?? 0) - (b.inventory         ?? 0),
    days_on_hand:     (a, b) => (a.days_on_hand      ?? 0) - (b.days_on_hand      ?? 0),
    inventory_status: (a, b) => (a.inventory_status || '').localeCompare(b.inventory_status || ''),
};

// ─── Products component ───────────────────────────────────────────────────────

const Products = ({
                      userData,
                      selectedMarketplace,
                      selectedCurrencySign,
                      selectedTileData,
                      onColumnsReady,
                      onFiltersReady,
                      onFilteredAsinsChange,
                  }) => {

    const [loading, setLoading]                   = useState(false);
    const [allProducts, setAllProducts]           = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [totals, setTotals]                     = useState(null);
    const [isModalOpen, setIsModalOpen]           = useState(false);
    const [selectedRow, setSelectedRow]           = useState(null);
    const [isMobile, setIsMobile]                 = useState(window.innerWidth < 768);
    const [activeFilters, setActiveFilters]       = useState([]);
    // sortState drives full-dataset sorting (see sortedProducts below).
    // { columnKey: string|null, order: 'ascend'|'descend'|null }
    const [sortState, setSortState]               = useState({ columnKey: null, order: null });
    const [page, setPage]                         = useState(1);
    const [pageSize, setPageSize]                 = useState(defaultPageSize);

    const [apiFilter, setApiFilter] = useState({
        marketplace_id: selectedMarketplace?.id,
        start_date:     selectedTileData?.start_date,
        end_date:       selectedTileData?.end_date,
        sku:            selectedTileData?.sku,
    });

    const isFirstRender = useRef(true);

    // ── FIX (PATH C): deduplication ref ──────────────────────────────────────
    // Tracks the filter-key of the request currently in-flight.
    // If fetchProducts is called again with the same key before the first
    // resolves (e.g. React Strict Mode double-invocation), the second call
    // is a no-op — preventing the duplicate network request.
    const inflight = useRef(null);

    const handleResize = useCallback(() => setIsMobile(window.innerWidth < 768), []);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const openDetails = useCallback(record => {
        setSelectedRow(record);
        setIsModalOpen(true);
    }, []);

    // ─── Filter mutation handlers ─────────────────────────────────────────────
    const addFilter = useCallback(filterObj => {
        setActiveFilters(prev => [...prev, filterObj]);
        setPage(1);
    }, []);

    const removeFilter = useCallback(id => {
        setActiveFilters(prev => prev.filter(f => f.id !== id));
        setPage(1);
    }, []);

    const updateFilter = useCallback((id, updatedFields) => {
        setActiveFilters(prev =>
            prev.map(f => f.id === id ? { ...f, ...updatedFields } : f)
        );
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setActiveFilters([]);
        setPage(1);
    }, []);

    // Captures sort column + direction from antd's Table onChange.
    // We use sortOrder (controlled mode) on columns so antd renders the
    // indicator but does NOT sort the data itself — we sort manually below.
    const handleTableChange = useCallback((_, __, sorter) => {
        setSortState({
            columnKey: sorter.order ? sorter.columnKey : null,
            order:     sorter.order || null,
        });
        setPage(1); // always reset to page 1 when sort changes
    }, []);

    // ─── Re-apply filters + notify parent + refresh totals ───────────────────
    // ── FIX (PATH B) ─────────────────────────────────────────────────────────
    // Previously this effect called the totals API unconditionally whenever
    // `allProducts` changed — including on the initial load when fetchProducts
    // had JUST fetched the correct totals.  That produced a duplicate request.
    //
    // Rule: the totals API is only called here when column filters are
    // ACTIVELY applied (activeFilters.length > 0).  In that case the
    // server must re-aggregate for the filtered SKU subset.
    //
    // When there are no active filters the totals from fetchProducts are
    // already accurate; we simply skip the re-fetch.
    useEffect(() => {
        // 1. Client-side filtering — unchanged behaviour
        const result = applyFilters(allProducts, activeFilters);
        setFilteredProducts(result);
        setPage(1);

        // 2. Notify ChartComponent of the filtered SKU list
        if (typeof onFilteredAsinsChange === 'function') {
            onFilteredAsinsChange(
                activeFilters.length === 0
                    ? null
                    : result.map(p => p.sku).filter(Boolean)
            );
        }

        // 3. Re-fetch totals ONLY when column filters are active.
        //    Guards:
        //      a) activeFilters.length === 0  → no filters; fetchProducts owns
        //         correct totals already — calling here is a duplicate.
        //      b) allProducts.length === 0    → data not loaded yet; nothing
        //         meaningful to filter and apiFilter may be incomplete.
        //      c) !apiFilter?.marketplace_id  → missing context; skip.
        if (
            activeFilters.length === 0 ||
            allProducts.length  === 0 ||
            !apiFilter?.marketplace_id
        ) return;

        const skuParam = result.map(p => p.sku).filter(Boolean).join(',');

        MakeApiCall(
            `dashboard/tiles/get-product-analytics-data-total${ConvertParams({
                ...apiFilter,
                ...(skuParam ? { sku: skuParam } : {}),
            })}`,
            'get', null, true, {}, userData?.token
        )
            .then(res => { if (res?.status) setTotals(res.data); })
            .catch(() => { /* non-critical — keep last known totals */ });

    }, [activeFilters, allProducts]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Surface filter controls to ItemMaster ────────────────────────────────
    useEffect(() => {
        if (typeof onFiltersReady !== 'function') return;
        onFiltersReady({
            filterableColumns: FILTERABLE_COLUMNS,
            activeFilters,
            addFilter,
            updateFilter,
            removeFilter,
            clearFilters,
        });
    }, [activeFilters, addFilter, updateFilter, removeFilter, clearFilters, onFiltersReady]);

    // ─── Data fetching (localStorage-first) ──────────────────────────────────
    const fetchProducts = useCallback(async currentFilter => {
        if (!currentFilter?.marketplace_id) {
            setAllProducts([]);
            return;
        }

        const { start_date, end_date } = currentFilter;
        const userId   = userData?.user?.user_data?.id ?? userData?.user?.id ?? 'guest';
        const cacheKey = buildCacheKey(userId, start_date, end_date);

        // ── FIX (PATH C): skip if an identical request is already in-flight ──
        // This guards against React Strict Mode's intentional double-invocation
        // of effects (development only) and any other scenario where the same
        // filter key is submitted twice before the first resolves.
        if (inflight.current === cacheKey) return;
        inflight.current = cacheKey;

        // ── Cache hit ────────────────────────────────────────────────────────
        const cached = getProductCache(cacheKey);
        if (cached) {
            setAllProducts(cached);
            // Totals are lightweight; always refresh even on cache hit so
            // the summary row reflects the current date-range aggregation.
            try {
                const totalRes = await MakeApiCall(
                    `dashboard/tiles/get-product-analytics-data-total${ConvertParams(currentFilter)}`,
                    'get', null, true, {}, userData?.token
                );
                if (totalRes?.status) setTotals(totalRes.data);
            } catch { /* non-critical */ }
            finally { inflight.current = null; }
            return;
        }

        // ── Cache miss — fetch products + totals in parallel ─────────────────
        setLoading(true);
        try {
            const [dataRes, totalRes] = await Promise.all([
                MakeApiCall(
                    `dashboard/tiles/get-product-analytics-data${ConvertParams({
                        ...currentFilter,
                        no_pagination: true,
                    })}`,
                    'get', null, true, {}, userData?.token
                ),
                MakeApiCall(
                    `dashboard/tiles/get-product-analytics-data-total${ConvertParams(currentFilter)}`,
                    'get', null, true, {}, userData?.token
                ),
            ]);

            if (dataRes?.status) {
                const raw = dataRes.data?.records;
                const records = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.summaryData)
                        ? raw.summaryData
                        : [];
                setProductCache(cacheKey, records);
                setAllProducts(records);
            } else {
                message.warning(dataRes?.message || 'Failed to load product data');
            }

            if (totalRes?.status) setTotals(totalRes.data);

        } catch {
            message.error('Failed to load product data');
        } finally {
            setLoading(false);
            inflight.current = null;
        }
    }, [userData?.token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchProducts(apiFilter);
    }, [apiFilter, fetchProducts]);

    // Sync apiFilter when marketplace / date / sku changes from parent
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setActiveFilters([]);
        setSortState({ columnKey: null, order: null }); // reset sort on new date/marketplace
        setPage(1);
        setApiFilter({
            marketplace_id: selectedMarketplace?.id,
            start_date:     selectedTileData?.start_date,
            end_date:       selectedTileData?.end_date,
            sku:            selectedTileData?.sku,
        });
    }, [
        selectedMarketplace?.id,
        selectedTileData?.start_date,
        selectedTileData?.end_date,
        selectedTileData?.sku,
    ]);

    // ─── Sort full dataset, THEN paginate ────────────────────────────────────
    // sortedProducts = filteredProducts with sort applied across ALL rows.
    // pagedProducts  = current page slice of sortedProducts.
    // This ensures sorting is never limited to only the visible page.
    const sortedProducts = useMemo(() => {
        const { columnKey, order } = sortState;
        if (!columnKey || !order) return filteredProducts;          // no sort active
        const fn = COLUMN_SORTERS[columnKey];
        if (!fn) return filteredProducts;                           // unknown column
        const sorted = [...filteredProducts].sort(fn);
        return order === 'descend' ? sorted.reverse() : sorted;
    }, [filteredProducts, sortState]);

    const pagedProducts = useMemo(() => {
        const start = (page - 1) * pageSize;
        return sortedProducts.slice(start, start + pageSize);
    }, [sortedProducts, page, pageSize]);

    // ─── Column definitions ───────────────────────────────────────────────────
    const allColumns = useMemo(() => [
        {
            title: '#',
            key: 'index',
            width: 60,
            render: (_, __, index) => (page - 1) * pageSize + index + 1,
        },
        {
            title: 'Product Details',
            key: 'productDetails',
            fixed: 'left',
            width: 450,
            render: (_, record) => {
                const productUrl = record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : '#';
                return (
                    <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                        <a href={productUrl} target="_blank" rel="noopener noreferrer"
                           className="symbol symbol-65px overflow-hidden me-3 border border-gray-300 rounded-2 shadow-sm">
                            <img src={record?.image_url || fakeProImage} alt="product" style={{ objectFit: 'contain' }} />
                        </a>
                        <div className="d-flex flex-column">
                            <a className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-250px"
                               href={productUrl} target="_blank" rel="noopener noreferrer">
                                {record?.product_title || ''}
                            </a>
                            <div className="fs-7 mt-1">
                                <span><b>SKU:</b> {record?.sku}</span>{' / '}
                                <span><b>ASIN:</b> {record?.asin}</span>
                            </div>
                            <div className="fs-7 mt-1">
                                <span><b>{getAmount(record?.price, selectedCurrencySign)}</b></span>
                                {' / '}<span>{record?.fulfillment_channel}</span>
                                {' / '}<span>COGS {getAmount(record?.item_cogs, selectedCurrencySign)}</span>
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Tags',
            key: 'tags',
            dataIndex: 'tags',
            width: 160,
            render: (tags) => {
                if (!tags?.length) return <span className="text-muted fs-8">—</span>;
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {tags.map((tag, idx) => (
                            <Tag key={idx} style={{ color: '#888', borderColor: '#d9d9d9', background: '#f5f5f5', fontSize: '11px' }}>
                                {tag}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        { title: 'Units Sold',    key: 'total_units',   dataIndex: 'total_units',   sorter: COLUMN_SORTERS.total_units,      sortOrder: sortState.columnKey === 'total_units'   ? sortState.order : null },
        {
            title: 'Refund Units', key: 'refund_unit', dataIndex: 'refund_unit',
            sorter: COLUMN_SORTERS.refund_unit, sortOrder: sortState.columnKey === 'refund_unit' ? sortState.order : null,
            render: (val, record) => {
                const reasons = record?.refund_breakup?.reasons || [];
                return (
                    <Tooltip placement="top" color="#1e1e2d" title={
                        <div className="p-2" style={{ minWidth: '200px' }}>
                            {reasons.length > 0
                                ? reasons.map((item, idx) => (
                                    <div key={idx} className="d-flex justify-content-between gap-3 mb-1">
                                        <span className="text-capitalize">{item.reason.toLowerCase().replace(/_/g, ' ')}:</span>
                                        <b>{item.count}</b>
                                    </div>
                                ))
                                : <div className="text-muted">No specific reasons provided</div>}
                        </div>
                    }>
                        <span className="fw-bold cursor-pointer text-danger" style={{ borderBottom: '1px dotted #ff4d4f' }}>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Refund Cost', key: 'refund_cost', dataIndex: 'refund_cost',
            sorter: COLUMN_SORTERS.refund_cost, sortOrder: sortState.columnKey === 'refund_cost' ? sortState.order : null,
            render: (val, record) => {
                const breakdown = record?.more_data?.refund_cost || {};
                return (
                    <Tooltip placement="top" color="#1e1e2d" title={
                        <div className="p-2" style={{ minWidth: '220px' }}>
                            <div className="d-flex justify-content-between gap-3 mb-1"><span>Total:</span><b>{getAmount(breakdown.total ?? 0, selectedCurrencySign)}</b></div>
                            <div className="d-flex justify-content-between gap-3 mb-1"><span>Refunded Amount:</span><b>{getAmount(breakdown.refunded_amount ?? 0, selectedCurrencySign)}</b></div>
                            <div className="d-flex justify-content-between gap-3 mb-1"><span>Fixed Closing Fee:</span><b>{getAmount(breakdown.fixed_closing_fee ?? 0, selectedCurrencySign)}</b></div>
                            <div className="d-flex justify-content-between gap-3"><span>Ship Promotion:</span><b>{getAmount(breakdown.ship_promotion ?? 0, selectedCurrencySign)}</b></div>
                        </div>
                    }>
                        <span className="fw-bold cursor-pointer text-danger" style={{ borderBottom: '1px dotted #ff4d4f' }}>{getAmount(val, selectedCurrencySign)}</span>
                    </Tooltip>
                );
            },
        },
        { title: 'Sales',         key: 'sales',         dataIndex: 'sales',         sorter: COLUMN_SORTERS.sales,            sortOrder: sortState.columnKey === 'sales'         ? sortState.order : null, render: (val) => <b className="text-gray-800">{getAmount(val, selectedCurrencySign)}</b> },
        { title: 'Ad Sales',      key: 'ad_sales',      dataIndex: 'ad_sales',      sorter: COLUMN_SORTERS.ad_sales,         sortOrder: sortState.columnKey === 'ad_sales'      ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: 'Ad Spend',      key: 'ad_spend',      dataIndex: 'ad_spend',      sorter: COLUMN_SORTERS.ad_spend,         sortOrder: sortState.columnKey === 'ad_spend'      ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: 'COGS',          key: 'cogs',          dataIndex: 'cogs',          sorter: COLUMN_SORTERS.cogs,             sortOrder: sortState.columnKey === 'cogs'          ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: 'Organic Sales', key: 'organic_sales', dataIndex: 'organic_sales', sorter: COLUMN_SORTERS.organic_sales,    sortOrder: sortState.columnKey === 'organic_sales' ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        {
            title: 'Tax', key: 'tax', dataIndex: ['more_data', 'tax_liability', 'total'],
            sorter: COLUMN_SORTERS.tax, sortOrder: sortState.columnKey === 'tax' ? sortState.order : null,
            render: (val) => getAmount(val || 0, selectedCurrencySign),
        },
        { title: 'Fees',         key: 'fees',         dataIndex: 'fees',         sorter: COLUMN_SORTERS.fees,             sortOrder: sortState.columnKey === 'fees'          ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: 'Gross Profit', key: 'gross_profit', dataIndex: 'gross_profit', sorter: COLUMN_SORTERS.gross_profit,      sortOrder: sortState.columnKey === 'gross_profit'  ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: 'Net Profit',   key: 'net_profit',   dataIndex: 'net_profit',   sorter: COLUMN_SORTERS.net_profit,       sortOrder: sortState.columnKey === 'net_profit'    ? sortState.order : null, render: (val) => <span className="fw-bold text-success">{getAmount(val, selectedCurrencySign)}</span> },
        {
            title: 'Inventory', key: 'inventory', dataIndex: 'inventory',
            sorter: COLUMN_SORTERS.inventory, sortOrder: sortState.columnKey === 'inventory' ? sortState.order : null,
            render: (val, record) => {
                const inv = record?.more_data?.total_inventories || {};
                return (
                    <Tooltip placement="top" color="#1e1e2d" title={
                        <div className="p-2" style={{ minWidth: '180px' }}>
                            <div>FBA Available: <b>{inv.fba_available_inventory ?? 0}</b></div>
                            <div>Reserved: <b>{inv.reserved_inventory ?? 0}</b></div>
                            <div>Inbound: <b>{inv.inbound_inventory ?? 0}</b></div>
                        </div>
                    }>
                        <span className="fw-bold cursor-pointer" style={{ borderBottom: '1px dotted #adadad' }}>{val}</span>
                    </Tooltip>
                );
            },
        },
        { title: 'Days Until Stockout', key: 'days_on_hand', dataIndex: 'days_on_hand', sorter: COLUMN_SORTERS.days_on_hand,      sortOrder: sortState.columnKey === 'days_on_hand'  ? sortState.order : null },
        {
            title: 'Inventory Health', key: 'inventory_status', dataIndex: 'inventory_status',
            sorter: COLUMN_SORTERS.inventory_status, sortOrder: sortState.columnKey === 'inventory_status' ? sortState.order : null,
            render: (status) => {
                let badgeClass = 'badge-light-primary';
                const lower = status?.toLowerCase() || '';
                if (lower.includes('in stock') || lower === 'healthy')        badgeClass = 'badge-light-success';
                else if (lower.includes('low') || lower.includes('alert'))    badgeClass = 'badge-light-warning';
                else if (lower.includes('out') || lower.includes('overstock')) badgeClass = 'badge-light-danger';
                return <span className={`badge ${badgeClass} fw-bold px-4 py-3`}>{status || 'N/A'}</span>;
            },
        },
        {
            title: 'Status', key: 'status', dataIndex: 'status',
            render: status => (
                <span className={`badge ${status === 0 ? 'badge-light-success' : 'badge-light-danger'}`}>
                    {status === 0 ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            title: 'Details', key: 'details',
            render: (_, record) => (
                <span className="cursor-pointer fw-bold text-hover-danger" style={{ color: '#D8372E' }}
                      onClick={() => openDetails(record)}>
                    See Details
                </span>
            ),
        },
    ], [selectedCurrencySign, page, pageSize, openDetails, sortState]);

    // ─── Column visibility ────────────────────────────────────────────────────
    const [visibleColumns, setVisibleColumns] = useState(
        () => allColumns.map(col => col.key).filter(key => key !== 'tags')
    );

    const columns = useMemo(
        () => allColumns.filter(col => visibleColumns.includes(col.key)),
        [allColumns, visibleColumns]
    );

    const toggleableColumns = useMemo(
        () => allColumns.filter(col => col.key !== 'index' && col.key !== 'productDetails' && col.key !== 'details'),
        [allColumns]
    );

    const handleColumnToggle = useCallback(checkedValues => {
        setVisibleColumns(['index', 'productDetails', 'details', ...checkedValues]);
    }, []);

    useEffect(() => {
        if (!isMobile && onColumnsReady) {
            onColumnsReady({ toggleableColumns, visibleColumns, handleColumnToggle });
        }
    }, [toggleableColumns, visibleColumns, handleColumnToggle, isMobile, onColumnsReady]);

    useEffect(() => {
        if (isMobile && onColumnsReady) onColumnsReady(null);
    }, [isMobile, onColumnsReady]);

    // ─── Mobile cards ─────────────────────────────────────────────────────────
    const renderMobileCards = () => (
        <div className="d-flex flex-column gap-3">
            {pagedProducts.map((item, index) => (
                <div key={item.sku || index} className="card border-0 shadow-sm p-4 rounded-4 cursor-pointer"
                     style={{ background: '#fff', border: '1px solid #f0f0f0' }} onClick={() => openDetails(item)}>
                    <div className="d-flex align-items-center mb-4">
                        <img src={item.image_url || fakeProImage} alt="pro" className="rounded-2 me-3"
                             style={{ width: '50px', height: '50px', objectFit: 'contain', border: '1px solid #f5f5f5' }} />
                        <div className="overflow-hidden">
                            <div className="fw-bolder text-gray-800 text-truncate fs-6" style={{ maxWidth: '220px' }}>{item.product_title || 'N/A'}</div>
                            <div className="text-muted fs-8"><b>SKU:</b> {item.sku} <span className="mx-1">|</span> <b>ASIN:</b> {item.asin}</div>
                        </div>
                    </div>
                    <div className="row g-4 border-top pt-4">
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Units</div><div className="fw-bolder text-dark">{item.total_units}</div></div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Sales</div><div className="fw-bolder text-dark">{getAmount(item.sales, selectedCurrencySign)}</div></div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Net Profit</div><div className={`fw-bolder ${item.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>{getAmount(item.net_profit, selectedCurrencySign)}</div></div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Refunds</div>
                            <Tooltip trigger="click" color="#1e1e2d" title={
                                <div className="p-2" style={{ minWidth: '180px' }}>
                                    <div className="fw-bold mb-2 border-bottom pb-1" style={{ color: '#ff4d4f' }}>Refund Reasons</div>
                                    {(item?.refund_breakup?.reasons || []).length > 0
                                        ? item.refund_breakup.reasons.map((r, idx) => (
                                            <div key={idx} className="d-flex justify-content-between gap-3 mb-1 fs-8">
                                                <span className="text-capitalize">{r.reason.toLowerCase().replace(/_/g, ' ')}:</span>
                                                <b>{r.count}</b>
                                            </div>
                                        ))
                                        : <div className="fs-8 text-muted">No reasons provided</div>}
                                </div>
                            }>
                                <div className="fw-bolder text-danger" style={{ borderBottom: '1px dotted #ff4d4f', display: 'inline-block' }}>{item.refund_unit}</div>
                            </Tooltip>
                        </div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ref. Cost</div><div className="fw-bolder text-muted">{getAmount(item.refund_cost, selectedCurrencySign)}</div></div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ad Spend</div><div className="fw-bolder text-primary">{getAmount(item.ad_spend, selectedCurrencySign)}</div></div>
                    </div>
                </div>
            ))}
        </div>
    );

    // ─── Summary row ──────────────────────────────────────────────────────────
    const renderSummary = () => {
        if (!totals) return null;
        const summaryDataMap = {
            total_units:   totals.total_units,
            refund_unit:   totals.refund_unit,
            refund_cost:   getAmount(totals.refund_cost, selectedCurrencySign),
            sales:         <span className="text-dark">{getAmount(totals.sales, selectedCurrencySign)}</span>,
            ad_sales:      getAmount(totals.ad_sales, selectedCurrencySign),
            ad_spend:      getAmount(totals.ad_spend, selectedCurrencySign),
            cogs:          totals.cogs?.toFixed(2),
            organic_sales: getAmount(totals.organic_sales, selectedCurrencySign),
            tax:           getAmount(totals.tax, selectedCurrencySign),
            fees:          getAmount(totals.fees, selectedCurrencySign),
            gross_profit:  getAmount(totals.gross_profit, selectedCurrencySign),
            net_profit:    <span className={totals.net_profit >= 0 ? 'text-success' : 'text-danger'}>{getAmount(totals.net_profit, selectedCurrencySign)}</span>,
            inventory:     totals.inventory,
        };
        return (
            <Table.Summary fixed>
                <Table.Summary.Row className="bg-light fw-bolder">
                    {columns.map((col, index) => (
                        <Table.Summary.Cell key={col.key} index={index}>
                            {col.key === 'index' ? '' : col.key === 'productDetails' ? 'TOTALS' : (summaryDataMap[col.key] || '')}
                        </Table.Summary.Cell>
                    ))}
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    return (
        <div className="products-container">
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    {isMobile ? (
                        <div className="mobile-view-container px-1">
                            {pagedProducts.length > 0 ? renderMobileCards() : <Empty description="No Products Found" />}
                        </div>
                    ) : (
                        <div className="table-responsive shadow-sm rounded">
                            <Table
                                scroll={{ x: 'max-content' }}
                                columns={columns}
                                loading={loading}
                                dataSource={pagedProducts}
                                rowKey={record => record.sku || record.asin}
                                pagination={false}
                                summary={renderSummary}
                                locale={{ emptyText: <Empty description="No Products Found" /> }}
                                components={{ header: { row: props => <tr {...props} className="fw-bolder text-dark bg-cus" /> } }}
                                onChange={handleTableChange}
                                showSorterTooltip={false}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="card border-0 shadow-sm mt-5">
                <div className="card-body py-4">
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={filteredProducts.length}
                        showSizeChanger
                        onChange={(newPage, newPageSize) => { setPage(newPage); setPageSize(newPageSize); }}
                        showTotal={(total, range) => isMobile ? `Total: ${total}` : `${range[0]}-${range[1]} of ${total} products`}
                        responsive
                        size={isMobile ? 'small' : 'default'}
                        simple={isMobile}
                    />
                </div>
            </div>

            <ProductDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRow}
                currencySign={selectedCurrencySign}
            />
        </div>
    );
};

export default React.memo(Products);